import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, descargarArchivo } from '@/api/cliente'
import type {
  Cartera,
  CuentaDespacho,
  EventoDespacho,
  MiembroDespacho,
  PerfilDespacho,
  RespuestaInvitacion,
  RolDespacho,
  VinculoCliente,
  VinculoDespacho,
} from './tipos'

/**
 * Los hooks del despacho contable en el portal (`docs/adr/0021`).
 *
 * Dos lados y dos sesiones distintas:
 *
 *   `/interna/despacho/*` se pide con la **sesión del despacho**, la que no
 *   lleva contribuyente: perfil, equipo, clientes, cartera, lote y cuenta;
 *
 *   `/interna/contador/*` se pide con la **sesión del contribuyente**: qué
 *   despacho lleva su contabilidad, y aceptar, rechazar o revocar.
 *
 * Las escrituras van sin clave de idempotencia, como las de la consola: el
 * servidor no las deduplica y el transporte no reintenta lo que no lleva clave.
 * Repetir una invitación tampoco duplica nada: la base no deja dos vivas.
 */

export const clavesDespacho = {
  todo: ['despacho'] as const,
  perfil: ['despacho', 'perfil'] as const,
  equipo: ['despacho', 'equipo'] as const,
  clientes: ['despacho', 'clientes'] as const,
  cartera: ['despacho', 'cartera'] as const,
  cuenta: ['despacho', 'cuenta'] as const,
  auditoria: ['despacho', 'auditoria'] as const,
  contador: ['contador'] as const,
}

function invalidar(cliente: QueryClient): Promise<unknown> {
  return cliente.invalidateQueries({ queryKey: clavesDespacho.todo })
}

/* ---------------------------------------------------------------------- */
/* Sesión del despacho                                                      */
/* ---------------------------------------------------------------------- */

export function usePerfilDespacho() {
  return useQuery({
    queryKey: clavesDespacho.perfil,
    queryFn: ({ signal }) => api.obtener<PerfilDespacho>('/despacho', { senal: signal }),
    staleTime: 60_000,
  })
}

export function useEquipoDespacho() {
  return useQuery({
    queryKey: clavesDespacho.equipo,
    queryFn: ({ signal }) => api.obtener<MiembroDespacho[]>('/despacho/equipo', { senal: signal }),
  })
}

export function useVinculosDespacho() {
  return useQuery({
    queryKey: clavesDespacho.clientes,
    queryFn: ({ signal }) => api.obtener<VinculoDespacho[]>('/despacho/clientes', { senal: signal }),
  })
}

/**
 * La cartera. Cara de armar —entra a cada cliente— así que no se refresca
 * sola cada pocos segundos: cada cinco minutos, al volver a la pestaña y con
 * el botón de recargar.
 */
export function useCartera() {
  return useQuery({
    queryKey: clavesDespacho.cartera,
    queryFn: ({ signal }) => api.obtener<Cartera>('/despacho/cartera', { senal: signal }),
    staleTime: 60_000,
    refetchInterval: 300_000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
}

export function useCuentaDespacho() {
  return useQuery({
    queryKey: clavesDespacho.cuenta,
    queryFn: ({ signal }) => api.obtener<CuentaDespacho>('/despacho/cuenta', { senal: signal }),
  })
}

export function useAuditoriaDespacho(activa: boolean) {
  return useQuery({
    queryKey: clavesDespacho.auditoria,
    queryFn: ({ signal }) =>
      api.obtener<EventoDespacho[]>('/despacho/auditoria', { consulta: { limite: 200 }, senal: signal }),
    enabled: activa,
  })
}

export function useActualizarDespacho() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo: { nombre?: string; correo?: string }) => api.reemplazar<void>('/despacho', { cuerpo }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useAgregarMiembro() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo: { email: string; rol: RolDespacho; credencialInicial?: string }) =>
      api.crear<{ resultado: string; cuentaNueva: boolean }>('/despacho/equipo', { cuerpo }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useCambiarRolMiembro() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ miembro, rol }: { miembro: string; rol: RolDespacho }) =>
      api.reemplazar<void>(`/despacho/equipo/${encodeURIComponent(miembro)}/rol`, { cuerpo: { rol } }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useRetirarMiembro() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (miembro: string) => api.crear<void>(`/despacho/equipo/${encodeURIComponent(miembro)}/retirar`),
    onSuccess: () => invalidar(cliente),
  })
}

export function useInvitarCliente() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo: { rnc: string; mensaje?: string }) =>
      api.crear<RespuestaInvitacion>('/despacho/invitaciones', { cuerpo }),
    onSuccess: () => invalidar(cliente),
  })
}

export type AccionVinculo = 'retirar' | 'aceptar' | 'rechazar' | 'renunciar'

export function useAccionVinculo() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ vinculo, accion, motivo }: { vinculo: string; accion: AccionVinculo; motivo?: string }) =>
      api.crear<void>(`/despacho/vinculos/${encodeURIComponent(vinculo)}/${accion}`, {
        cuerpo: accion === 'rechazar' || accion === 'renunciar' ? { motivo } : undefined,
      }),
    onSuccess: () => invalidar(cliente),
  })
}

export function useAsignar() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ vinculo, miembros }: { vinculo: string; miembros: string[] }) =>
      api.reemplazar<void>(`/despacho/vinculos/${encodeURIComponent(vinculo)}/asignaciones`, { cuerpo: { miembros } }),
    onSuccess: () => invalidar(cliente),
  })
}

/** El ZIP con los TXT del período, de toda la cartera o de los elegidos. */
export function descargarLote(periodo: string, clientes?: string[]): Promise<void> {
  return descargarArchivo('/despacho/lote', {
    periodo,
    clientes: clientes && clientes.length > 0 ? clientes.join(',') : undefined,
  })
}

/* ---------------------------------------------------------------------- */
/* Registro, desde la sesión de un contribuyente                            */
/* ---------------------------------------------------------------------- */

export function useRegistrarDespacho() {
  return useMutation({
    mutationFn: (cuerpo: { nombre: string; identificacion: string; correo: string }) =>
      api.crear<{ despacho: string; verificado: boolean; digitoVerificadorAnomalo: boolean }>('/despachos', { cuerpo }),
  })
}

/* ---------------------------------------------------------------------- */
/* El lado del cliente                                                       */
/* ---------------------------------------------------------------------- */

export function useVinculosDelCliente() {
  return useQuery({
    queryKey: clavesDespacho.contador,
    queryFn: ({ signal }) => api.obtener<VinculoCliente[]>('/contador', { senal: signal }),
  })
}

export function useInvitarDespacho() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo: { identificacion: string; mensaje?: string }) =>
      api.crear<{ creada: boolean }>('/contador/invitaciones', { cuerpo }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: clavesDespacho.contador }),
  })
}

export type AccionDelCliente = 'aceptar' | 'rechazar' | 'retirar' | 'revocar'

export function useAccionDelCliente() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ vinculo, accion, motivo }: { vinculo: string; accion: AccionDelCliente; motivo?: string }) =>
      api.crear<void>(`/contador/vinculos/${encodeURIComponent(vinculo)}/${accion}`, {
        cuerpo: accion === 'rechazar' || accion === 'revocar' ? { motivo } : undefined,
      }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: clavesDespacho.contador }),
  })
}
